// frontend/src/pages/FeeConfigPage.tsx

import React, { useState, useEffect } from 'react';
import {
  Card,
  Descriptions,
  Form,
  InputNumber,
  Select,
  Button,
  Space,
  message,
  Typography,
  Divider,
  Alert,
  Row,
  Col,
  Statistic,
} from 'antd';
import { CalculatorOutlined } from '@ant-design/icons';
import * as feeConfigService from '../services/feeConfig';

const { Title, Text } = Typography;
const { Option } = Select;

const FeeConfigPage: React.FC = () => {
  const [feeConfig, setFeeConfig] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [feeBreakdown, setFeeBreakdown] = useState<any>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    loadFeeConfig();
  }, []);

  const loadFeeConfig = async () => {
    setLoading(true);
    try {
      const response = await feeConfigService.getFeeConfig();
      if (response.success) {
        setFeeConfig(response.data);
      }
    } catch (error: any) {
      message.error('加载费率配置失败');
    } finally {
      setLoading(false);
    }
  };

  const handleCalculate = async () => {
    try {
      const values = await form.validateFields();
      setCalculating(true);

      const response = await feeConfigService.calculateFeePreview({
        amount: values.amount * 100, // 转换为分
        currency: values.currency,
        payment_method_type: values.payment_method_type,
      });

      if (response.success) {
        setFeeBreakdown(response.data);
      }
    } catch (error: any) {
      if (error.errorFields) {
        return; // 表单验证错误
      }
      message.error('计算失败');
    } finally {
      setCalculating(false);
    }
  };

  const formatMoney = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount / 100);
  };

  const getFeeTypeLabel = (type: string) => {
    const types: Record<string, string> = {
      percentage: '百分比费率',
      fixed: '固定费用',
      combined: '组合费率',
    };
    return types[type] || type;
  };

  if (!feeConfig) {
    return <Card loading={loading}>加载中...</Card>;
  }

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <div>
          <Title level={3}>费率配置</Title>
          <Text type="secondary">查看您的商户费率配置 (由分组管理员设置)</Text>
        </div>

        <Card title="默认费率" loading={loading}>
          <Descriptions column={2} bordered>
            <Descriptions.Item label="费率类型">
              {getFeeTypeLabel(feeConfig.default_fee?.type)}
            </Descriptions.Item>

            {(feeConfig.default_fee?.type === 'percentage' ||
              feeConfig.default_fee?.type === 'combined') && (
              <Descriptions.Item label="百分比费率">
                {feeConfig.default_fee?.percentage_rate}%
              </Descriptions.Item>
            )}

            {(feeConfig.default_fee?.type === 'fixed' ||
              feeConfig.default_fee?.type === 'combined') && (
              <Descriptions.Item label="固定费用">
                {formatMoney(feeConfig.default_fee?.fixed_amount || 0)}
              </Descriptions.Item>
            )}

            {feeConfig.default_fee?.min_fee > 0 && (
              <Descriptions.Item label="最低费用">
                {formatMoney(feeConfig.default_fee?.min_fee)}
              </Descriptions.Item>
            )}

            {feeConfig.default_fee?.max_fee > 0 && (
              <Descriptions.Item label="最高费用">
                {formatMoney(feeConfig.default_fee?.max_fee)}
              </Descriptions.Item>
            )}

            {feeConfig.default_fee?.cap > 0 && (
              <Descriptions.Item label="费用上限">
                {formatMoney(feeConfig.default_fee?.cap)}
              </Descriptions.Item>
            )}

            <Descriptions.Item label="自定义费率">
              {feeConfig.custom_fee_enabled ? '已启用' : '未启用'}
            </Descriptions.Item>

            <Descriptions.Item label="客户承担费用">
              {feeConfig.pass_fee_to_customer ? '是' : '否'}
            </Descriptions.Item>
          </Descriptions>

          <Divider />

          <Alert
            message="费率说明"
            description={
              <div>
                <p>
                  当前费率配置由您所在的商户分组管理员设置。如需调整费率，请联系管理员。
                </p>
                {feeConfig.default_fee?.type === 'combined' && (
                  <p>
                    <strong>组合费率计算:</strong> 手续费 = (交易金额 ×{' '}
                    {feeConfig.default_fee?.percentage_rate}%) +{' '}
                    {formatMoney(feeConfig.default_fee?.fixed_amount || 0)}
                  </p>
                )}
              </div>
            }
            type="info"
            showIcon
          />
        </Card>

        <Card title="费用计算器">
          <Form form={form} layout="vertical" initialValues={{ currency: 'USD', payment_method_type: 'card' }}>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  label="交易金额"
                  name="amount"
                  rules={[
                    { required: true, message: '请输入交易金额' },
                    { type: 'number', min: 0.01, message: '金额必须大于0' },
                  ]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="请输入金额"
                    step={1}
                    precision={2}
                    addonBefore="$"
                  />
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item label="货币" name="currency" rules={[{ required: true }]}>
                  <Select placeholder="选择货币">
                    <Option value="USD">USD - 美元</Option>
                    <Option value="CNY">CNY - 人民币</Option>
                    <Option value="EUR">EUR - 欧元</Option>
                    <Option value="GBP">GBP - 英镑</Option>
                  </Select>
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  label="支付方式"
                  name="payment_method_type"
                  rules={[{ required: true }]}
                >
                  <Select placeholder="选择支付方式">
                    <Option value="card">Card - 银行卡</Option>
                    <Option value="alipay">Alipay - 支付宝</Option>
                    <Option value="wechat">WeChat - 微信支付</Option>
                    <Option value="crypto">Crypto - 加密货币</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item>
              <Button
                type="primary"
                icon={<CalculatorOutlined />}
                onClick={handleCalculate}
                loading={calculating}
              >
                计算手续费
              </Button>
            </Form.Item>
          </Form>

          {feeBreakdown && (
            <>
              <Divider />
              <Row gutter={16}>
                <Col span={6}>
                  <Statistic
                    title="交易金额"
                    value={formatMoney(feeBreakdown.amount, feeBreakdown.currency)}
                    precision={2}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="手续费"
                    value={formatMoney(feeBreakdown.fee, feeBreakdown.currency)}
                    precision={2}
                    valueStyle={{ color: '#cf1322' }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="净收入"
                    value={formatMoney(feeBreakdown.net_amount, feeBreakdown.currency)}
                    precision={2}
                    valueStyle={{ color: '#3f8600' }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="费率"
                    value={
                      feeBreakdown.percentage_rate
                        ? `${feeBreakdown.percentage_rate}%`
                        : '-'
                    }
                  />
                </Col>
              </Row>

              <Divider />

              <Alert
                message="计算详情"
                description={
                  <div>
                    <p>
                      <strong>费率类型:</strong> {getFeeTypeLabel(feeBreakdown.fee_type)}
                    </p>
                    {feeBreakdown.percentage_rate && (
                      <p>
                        <strong>百分比费率:</strong> {feeBreakdown.percentage_rate}%
                      </p>
                    )}
                    {feeBreakdown.fixed_amount > 0 && (
                      <p>
                        <strong>固定费用:</strong>{' '}
                        {formatMoney(feeBreakdown.fixed_amount, feeBreakdown.currency)}
                      </p>
                    )}
                    <p>
                      <strong>支付方式:</strong> {feeBreakdown.payment_method_type}
                    </p>
                  </div>
                }
                type="success"
              />
            </>
          )}
        </Card>
      </Space>
    </div>
  );
};

export default FeeConfigPage;
